<script setup lang="ts">
import { useRouter } from "#app";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import NavBarItem from "~/components/Navbar/NavBarItem.vue";
import NavBarSection from "~/components/Navbar/NavBarSection.vue";
import { useNavbar } from "~/composables/navbar/useNavbar";
import { useAuthStore } from "~/store/auth/auth.store";
import { useCompaniesStore } from "~/store/companies/companies.store";

const router = useRouter();
const authStore = useAuthStore();
const companiesStore = useCompaniesStore();
const { companyInfos } = storeToRefs(companiesStore);
const config = useRuntimeConfig();
const { isCollapsed, toggleCollapsed } = useNavbar();

const toggleLabel = computed<string>(() => (isCollapsed.value ? "Agrandir le menu" : "Réduire le menu"));

// Whole path segments only: a route nested under an entry lights it up, a query or a longer name never does.
const isActive = (path: string): boolean => {
  const current = router.currentRoute.value.path;
  return current === path || current.startsWith(`${path}/`);
};

onMounted(() => {
  // Only some pages load the company infos, and the menu shows its logo on all of them.
  if (!companyInfos.value) companiesStore.fetchCompanyInfos();
});
</script>

<template>
  <div
    class="navbar fixed flex h-[var(--app-viewport-height)] w-navbar-width flex-col gap-5 overflow-hidden border-r border-stroke-color bg-gradient-to-b from-mist-blue-color via-mist-grey-color via-50% to-mist-grey-color px-3 pb-3 pt-4 max-[768px]:hidden"
  >
    <!-- The paddings make the 48 px collapsed row centre the toggle once the client block has shrunk to nothing. -->
    <div
      class="flex shrink-0 items-center gap-[10px] py-1 transition-[padding] duration-200 ease-out motion-reduce:transition-none"
      :class="isCollapsed ? 'pr-[10px]' : 'pl-1.5'"
    >
      <!-- Capped at the toggle's height once collapsed: the hidden 32 px logo would keep the row 4 px taller. -->
      <div
        class="flex min-w-0 flex-1 items-center gap-[10px] overflow-hidden transition-[opacity,max-height] duration-200 ease-out motion-reduce:transition-none"
        :class="isCollapsed ? 'max-h-7 opacity-0' : 'max-h-8'"
      >
        <img
          v-if="companyInfos?.logo"
          :src="companyInfos.logo"
          alt=""
          class="size-8 shrink-0 rounded-lg object-cover"
        />
        <span class="body_demi truncate text-primary-color">{{ authStore.user?.mainEntityId?.name }}</span>
      </div>
      <!-- No open-on-focus: the button keeps focus after a click, which would pin its tooltip open. -->
      <Tooltip class="shrink-0" position="mid-right" append-to-body minified :force-position-left="8">
        <template #img>
          <button
            type="button"
            class="flex size-7 items-center justify-center rounded-[6px] text-secondary-color hover:bg-lightest-light-grey-color"
            :aria-label="toggleLabel"
            :aria-expanded="!isCollapsed"
            @click="toggleCollapsed"
          >
            <SvgIcon :name="isCollapsed ? 'menu-expand' : 'menu-collapse'" class="size-[18px]" />
          </button>
        </template>
        <template #content>
          <span class="small_med whitespace-nowrap">{{ toggleLabel }}</span>
        </template>
      </Tooltip>
    </div>

    <div
      class="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <NavBarSection :collapsed="isCollapsed">
        <NavBarItem
          to="/home"
          label="Accueil"
          icon="home-menu"
          active-icon="home-plain-menu"
          :active="isActive('/home')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          to="/catalog"
          label="Catalogue"
          icon="search-menu"
          active-icon="search-plain-menu"
          :active="isActive('/catalog')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          to="/my-trainings"
          label="Mon activité"
          icon="graduate-cap-menu"
          active-icon="graduate-cap-plain-menu"
          :active="isActive('/my-trainings')"
          :collapsed="isCollapsed"
        />
      </NavBarSection>

      <NavBarSection
        v-if="!authStore.isCompanyAdmin && (authStore.isTrainingPlanAdmin || authStore.isTrainingAdmin)"
        title="Référent"
        :collapsed="isCollapsed"
      >
        <NavBarItem
          v-if="authStore.isTrainingAdmin"
          to="/my-catalog"
          label="Gérer mes formations"
          icon="books-menu"
          active-icon="books-plain-menu"
          :active="isActive('/my-catalog')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          v-if="authStore.isTrainingPlanAdmin || authStore.isTrainingAdmin"
          to="/my-sessions"
          label="Organiser une session"
          icon="calendar-menu"
          active-icon="calendar-plain-menu"
          :active="isActive('/my-sessions')"
          :collapsed="isCollapsed"
        />
      </NavBarSection>

      <NavBarSection v-if="authStore.isInternalTrainer" title="FORMATEUR" :collapsed="isCollapsed">
        <NavBarItem
          to="/trainer-activities"
          label="Mes interventions"
          icon="briefcase"
          :active="isActive('/trainer-activities')"
          :collapsed="isCollapsed"
        />
      </NavBarSection>

      <NavBarSection
        v-if="authStore.isCompanyAdmin || authStore.isGroupAdmin"
        title="PLAN DE FORMATION"
        :collapsed="isCollapsed"
      >
        <NavBarItem
          to="/sessions"
          label="Sessions"
          icon="calendar-menu"
          active-icon="calendar-plain-menu"
          :active="isActive('/sessions')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          to="/registrations"
          label="Inscriptions"
          icon="indiv"
          active-icon="indiv-plain-menu"
          :active="isActive('/registrations')"
          :collapsed="isCollapsed"
        />
      </NavBarSection>

      <NavBarSection
        v-if="authStore.isCompanyAdmin || authStore.isGroupAdmin || authStore.isManager || authStore.isGroupViewer"
        :title="authStore.isManager && !(authStore.isCompanyAdmin || authStore.isGroupAdmin) ? 'MON ÉQUIPE' : 'GESTION'"
        :collapsed="isCollapsed"
      >
        <NavBarItem
          to="/requests"
          label="Recueil des besoins"
          icon="request-menu"
          active-icon="request-plain-menu"
          :active="isActive('/requests')"
          :collapsed="isCollapsed"
        />
        <!-- Managers and animateurs de groupe keep a single "Plan de formation" entry here -->
        <NavBarItem
          v-if="!(authStore.isCompanyAdmin || authStore.isGroupAdmin)"
          to="/registrations"
          label="Plan de formation"
          icon="target-menu"
          active-icon="target-plain-menu"
          :active="isActive('/registrations')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          to="/habilitations"
          label="Habilitations"
          icon="medal"
          active-icon="medal-plain-menu"
          :active="isActive('/habilitations')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          v-if="(authStore.isCompanyAdmin || authStore.isGroupAdmin) && companyInfos?.learningPaths"
          to="/learning-path"
          label="Parcours"
          icon="graduate-cap-menu"
          active-icon="graduate-cap-plain-menu"
          :active="isActive('/learning-path')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          to="/evaluations"
          label="Évaluations"
          icon="star-menu"
          active-icon="star"
          :active="isActive('/evaluations')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          v-if="authStore.canAccessDashboard"
          to="/dashboard"
          label="Tableau de bord"
          icon="chart-pie-menu"
          active-icon="chart-pie-plain-menu"
          :active="isActive('/dashboard')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          v-if="authStore.isCompanyAdmin || authStore.isGroupAdmin"
          to="/company-catalog"
          label="Gestion catalogue"
          icon="table"
          active-icon="table-plain-menu"
          :active="isActive('/company-catalog')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          to="/users"
          label="Utilisateurs"
          icon="group-menu"
          active-icon="group-plain-menu"
          :active="isActive('/users')"
          :collapsed="isCollapsed"
        />
        <NavBarItem
          v-if="authStore.isSimbelAdmin && config.public.ENV !== 'PROD'"
          to="/charts"
          label="Graphiques IA"
          icon="chart-ai-menu"
          :active="isActive('/charts')"
          :collapsed="isCollapsed"
        />
      </NavBarSection>
    </div>

    <div v-if="authStore.isCompanyAdmin" class="navbar_footer flex shrink-0 flex-col gap-2.5">
      <div class="h-px bg-stroke-color"></div>
      <NavBarItem
        to="/settings"
        label="Paramètres"
        icon="settings-menu"
        active-icon="settings-plain-menu"
        :active="isActive('/settings')"
        :collapsed="isCollapsed"
      />
    </div>
  </div>
</template>
